import { CopyError } from '../copy/text.js';
import { viewerStrings } from '../copy/strings.js';
import type { ImportProgress } from '@cdr-clarity/contracts';
import type { DataFailure, DataRequest, DataResponse, Results } from './protocol.js';

export interface DataPort {
  postMessage(
    message: { type: 'request'; id: number; request: DataRequest } | { type: 'cancel'; id: number },
  ): void;
  addEventListener(type: 'message', listener: (event: MessageEvent<DataResponse>) => void): void;
  addEventListener(type: 'error', listener: (event: ErrorEvent) => void): void;
  terminate(): void;
}
export class LocalDataError extends Error {
  constructor(readonly failure: DataFailure) {
    super(failure.detail);
    this.name = 'LocalDataError';
  }
}
interface Pending {
  id: number;
  request: DataRequest;
  progress: (value: ImportProgress) => void;
  resolve(value: Results[keyof Results]): void;
  reject(error: Error): void;
}

/** Serializes bounded UI requests; no records are retained by the transport after resolution. */
export class WorkerClient {
  private nextId = 0;
  private active: Pending | null = null;
  private queue: Pending[] = [];
  private stopped = false;
  constructor(private readonly port: DataPort) {
    port.addEventListener('message', (event) => this.receive(event.data));
    port.addEventListener('error', () =>
      this.dispose(viewerStrings.webWorkerClient.theanalysisworkerstoppedunexpectedlyReloadyour),
    );
  }
  request<K extends DataRequest['operation']>(
    request: Extract<DataRequest, { operation: K }>,
    progress: (value: ImportProgress) => void = () => {},
  ): Promise<Results[K]> {
    if (this.stopped)
      return Promise.reject(
        new CopyError(viewerStrings.webWorkerClient.theanalysissessionisclosed),
      );
    if (this.queue.length >= 32)
      return Promise.reject(
        new CopyError(viewerStrings.webWorkerClient.toomanypendinglocaloperations),
      );
    return new Promise<Results[K]>((resolve, reject) => {
      this.queue.push({
        id: ++this.nextId,
        request,
        progress,
        // receive verifies the response operation matches this request before resolving.
        resolve: (value) => resolve(value as Results[K]),
        reject,
      });
      this.advance();
    });
  }
  cancel(): void {
    for (const item of this.queue)
      item.reject(new CopyError(viewerStrings.webWorkerClient.localoperationcancelled));
    this.queue = [];
    if (this.active) this.port.postMessage({ type: 'cancel', id: this.active.id });
  }
  dispose(message = viewerStrings.webWorkerClient.theanalysissessionisclosed): void {
    this.stopped = true;
    this.active?.reject(new Error(message));
    for (const item of this.queue) item.reject(new Error(message));
    this.active = null;
    this.queue = [];
    this.port.terminate();
  }
  private advance(): void {
    if (this.active || this.stopped) return;
    this.active = this.queue.shift() ?? null;
    if (!this.active) return;
    try {
      this.port.postMessage({ type: 'request', id: this.active.id, request: this.active.request });
    } catch {
      this.dispose(viewerStrings.webWorkerClient.therequestcouldnotbesentto);
    }
  }
  private receive(response: DataResponse): void {
    if (!this.active || response.id !== this.active.id) return;
    if (response.type === 'progress') {
      this.active.progress(response.value);
      return;
    }
    const request = this.active;
    this.active = null;
    if (response.type === 'error') request.reject(new LocalDataError(response.error));
    else if (response.operation !== request.request.operation)
      request.reject(
        new CopyError(
          viewerStrings.webWorkerClient.theanalysisworkerreturnedanincompatibleresponse,
        ),
      );
    else request.resolve(response.value);
    this.advance();
  }
}
